<script setup>
import { ref, computed } from 'vue';
import Button from "../../common/Button.vue";
import GeminiIcon from "./GeminiIcon.vue";

const props = defineProps({
    activeTable: {
        type: String,
        default: 'Articles'
    }
});

// --- State ---
const showOffcanvas = ref(false);
const dailyUsed = ref(42500);
const dailyLimit = ref(50000);
const historyLogs = ref([
    { id: 1, action: 'SEO Optimization', timestamp: '10:15 AM', tokens: 1204 },
    { id: 2, action: 'Tone Change (Formal)', timestamp: '9:30 AM', tokens: 850 },
]);

const allMcpTools = ref([
    {
        name: 'SEO Booster',
        table: 'articles',
        samples: ["Write a meta description", "Suggest tags", "Check readability"]
    },
    {
        name: 'User Auditor',
        table: 'users',
        samples: ["Identify inactive users", "Summarize user permissions"]
    },
    {
        name: 'Global Tool',
        table: 'any',
        samples: ["Summarize selection", "Fix grammar"]
    }
]);

// --- Computed ---
const usagePercentage = computed(() => {
    const perc = (dailyUsed.value / dailyLimit.value) * 100;
    return perc > 100 ? 100 : perc;
});

const isOverLimit = computed(() => dailyUsed.value >= dailyLimit.value);

const contextualPrompts = computed(() => {
    // We normalize to lowercase to match the tool definitions
    const active = props.activeTable.toLowerCase();
    const filtered = allMcpTools.value.filter(t => t.table === active || t.table === 'any');
    return filtered.flatMap(t => t.samples);
});

const tableIcon = computed(() => {
    const icons = {
        articles: 'bi-file-earmark-text',
        users: 'bi-people',
        settings: 'bi-gear',
        general: 'bi-stars'
    };
    return icons[props.activeTable.toLowerCase()] || 'bi-table';
});

// --- Methods ---
const usePrompt = (text) => {
    // Logic to auto-fill the AI chat box or execute immediately
    console.log("Gemini Executing Prompt:", text);
};
</script>

<template>
    <div>
        <Button class="px-2 h-100 rounded-0" title="Show help" @click.native="showOffcanvas = true">
            <GeminiIcon/>
        </Button>

        <div class="offcanvas offcanvas-end w-75" :class="{ 'show': showOffcanvas }" tabindex="-1"
             :style="{ visibility: showOffcanvas ? 'visible' : 'hidden' }">
            <div class="offcanvas-header border-bottom bg-white">
                <div class="d-flex align-items-center">
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" class="me-2">
                        <path d="M12 0L14.59 7.41L22 10L14.59 12.59L12 20L9.41 12.59L2 10L9.41 7.41L12 0Z"
                              fill="url(#geminiGradient)"/>
                        <defs>
                            <linearGradient id="geminiGradient" x1="2" y1="2" x2="22" y2="20">
                                <stop stop-color="#4285F4"/>
                                <stop offset="1" stop-color="#9b72cb"/>
                            </linearGradient>
                        </defs>
                    </svg>
                    <h5 class="offcanvas-title fw-bold">Gemini Studio</h5>
                </div>
                <button type="button" class="btn-close" @click="showOffcanvas = false"></button>
            </div>

            <div class="offcanvas-body p-0 d-flex flex-column flex-md-row">
                <aside class="bg-light border-end d-flex flex-column p-3 col-md-3">
                    <h6 class="text-uppercase small fw-bold text-muted mb-3">Activity History</h6>

                    <div class="flex-grow-1 overflow-auto">
                        <div v-for="log in historyLogs" :key="log.id"
                             class="mb-3 p-2 bg-white rounded border-sm shadow-sm">
                            <div class="d-flex justify-content-between align-items-start">
                                <small class="fw-bold d-block">{{ log.action }}</small>
                                <i class="bi bi-check-circle-fill text-success small"></i>
                            </div>
                            <small class="text-muted" style="font-size: 0.75rem;">
                                {{ log.timestamp }} • {{ log.tokens.toLocaleString() }} tokens
                            </small>
                        </div>
                    </div>

                    <div class="border-top pt-3 mt-3">
                        <div class="d-flex justify-content-between align-items-center mb-1">
                            <span class="small fw-bold">Daily AI Credits</span>
                            <span class="small" :class="isOverLimit ? 'text-danger' : 'text-primary'">
                                {{ dailyUsed.toLocaleString() }} / {{ dailyLimit.toLocaleString() }}
                            </span>
                        </div>
                        <div class="progress" style="height: 8px;">
                            <div
                                class="progress-bar"
                                :class="isOverLimit ? 'bg-danger' : 'bg-primary'"
                                role="progressbar"
                                :style="{ width: usagePercentage + '%' }"
                            ></div>
                        </div>
                        <p v-if="isOverLimit" class="text-danger small mt-2 mb-0">
                            <i class="bi bi-exclamation-triangle"></i> Daily limit reached.
                        </p>
                        <p v-else class="text-muted small mt-2 mb-0">Resets at midnight</p>
                    </div>
                </aside>

                <main class="flex-grow-1 p-4 position-relative">
                    <div v-if="isOverLimit"
                         class="position-absolute w-100 h-100 top-0 start-0 d-flex align-items-center justify-content-center bg-white bg-opacity-75"
                         style="z-index: 10;">
                        <div class="text-center p-5 shadow rounded bg-white border">
                            <i class="bi bi-lock text-muted display-4"></i>
                            <h3>Daily Limit Reached</h3>
                            <p class="text-muted">You've used your allotted AI credits for today.<br>Come back tomorrow
                                or contact your admin.</p>
                            <button class="btn btn-outline-secondary" @click="showOffcanvas = false">Close Studio
                            </button>
                        </div>
                    </div>

                    <div class="d-flex align-items-center mb-4">
                        <div class="p-2 bg-light rounded-circle me-3">
                            <i :class="tableIcon" class="text-primary fs-4"></i>
                        </div>
                        <div>
                            <h4 class="mb-0">Working on {{ activeTable }}</h4>
                            <small class="text-muted">AI context is synced to your current view</small>
                        </div>
                    </div>

                    <section class="mb-5">
                        <h6 class="text-uppercase small fw-bold text-secondary mb-3">
                            <i class="bi bi-lightbulb me-1"></i> Suggested Prompts
                        </h6>
                        <div class="row g-2">
                            <div v-for="(prompt, index) in contextualPrompts" :key="index" class="col-md-6 col-lg-4">
                                <div
                                    class="card h-100 prompt-card border-dashed"
                                    @click="usePrompt(prompt)"
                                    role="button"
                                >
                                    <div class="card-body p-2 d-flex align-items-center">
                                        <i class="bi bi-plus-circle text-muted me-2"></i>
                                        <span class="small text-dark">{{ prompt }}</span>
                                    </div>
                                </div>
                            </div>
                            <div v-if="contextualPrompts.length === 0" class="col-12">
                                <p class="text-muted small italic">Select a table to see specialized AI tools.</p>
                            </div>
                        </div>
                    </section>

                    <section>
                        <h6 class="text-uppercase small fw-bold text-secondary mb-3">Available Tools</h6>
                    </section>
                </main>
            </div>
        </div>
        <div v-if="showOffcanvas" class="offcanvas-backdrop fade show" @click="showOffcanvas = false"></div>
    </div>
</template>

<style scoped>
.border-dashed {
    border: 1px dashed #dee2e6;
    transition: all 0.2s ease;
}

.prompt-card:hover {
    border-color: #4285F4;
    background-color: #f8faff;
    transform: translateY(-2px);
}

.offcanvas {
    transition: transform 0.3s ease-in-out, visibility 0.3s ease-in-out;
}
</style>